// 초안 본문·요약의 머리표 한 벌 — 초안 생성이 쓰고, 공개 화면이 끈 공개 항목의 줄을 같은 상수로 걷는다(저장값은 그대로).

import type { PortfolioPublicField } from "./types";

export const DRAFT_HEADING = {
  period: "[공사 기간]",
  phases: "[공정]",
  scope: "[시공 범위]",
  logs: "[현장 기록]",
} as const;

// 공간별 전/후 쌍 섹션 — 공개 항목과 무관하게 늘 보인다(`pairs.ts`).
export const DRAFT_PAIRS_HEADING = "[공간별 공사 전·후]";

// 공정·현장 기록 줄 머리 · 줄 상한을 넘겨 자른 자리.
export const DRAFT_ITEM = "· ";
export const DRAFT_ELLIPSIS = "…";

export const DRAFT_SUMMARY_LABEL = { period: "공사기간", phases: "공정", scope: "범위" } as const;
export const DRAFT_SUMMARY_SEP = " · ";

// 「2026-05-01 ~ 2026-05-12 (12일)」 — 날짜가 없는 쪽은 「?」.
const PERIOD_TEXT = "(?:\\d{4}-\\d{2}-\\d{2}|\\?) ~ (?:\\d{4}-\\d{2}-\\d{2}|\\?)(?: \\(\\d+일\\))?";
const PERIOD_LINE_RE = new RegExp(`^${PERIOD_TEXT}$`);

const isItemLine = (line: string): boolean => line.startsWith(DRAFT_ITEM) || line === DRAFT_ELLIPSIS;

interface BodySection {
  heading: string;
  field: PortfolioPublicField;
  isLine: (line: string) => boolean;
  maxLines: number;
}

// 「[시공 범위]」 는 늘 보인다 · 현장 기록은 「공정 챕터」 를 따른다 · 숨긴 섹션 아래 「· 」 줄은 사용자가 덧붙였어도 같이 숨고, 빈 줄 뒤 문단은 남는다.
const BODY_SECTIONS: readonly BodySection[] = [
  { heading: DRAFT_HEADING.period, field: "period", isLine: (line) => PERIOD_LINE_RE.test(line), maxLines: 1 },
  { heading: DRAFT_HEADING.phases, field: "timeline", isLine: isItemLine, maxLines: Number.POSITIVE_INFINITY },
  { heading: DRAFT_HEADING.logs, field: "timeline", isLine: isItemLine, maxLines: Number.POSITIVE_INFINITY },
];

const SUMMARY_PARTS: readonly { field: PortfolioPublicField; re: RegExp }[] = [
  { field: "period", re: new RegExp(`^${DRAFT_SUMMARY_LABEL.period} ${PERIOD_TEXT}$`) },
  { field: "timeline", re: new RegExp(`^${DRAFT_SUMMARY_LABEL.phases} \\d+개$`) },
];

// 머리표 줄(줄 전체가 머리표) + 생성 모양의 내용 줄 + 딸린 빈 줄 하나를 걷는다 — 내용 줄이 생성 모양이 아니면 사용자가 쓴 글로 보고 둔다.
const hideSections = (body: string, fields: readonly PortfolioPublicField[]): string => {
  const hidden = BODY_SECTIONS.filter((section) => !fields.includes(section.field));
  if (hidden.length === 0) return body;
  const lines = body.split(/\r?\n/);
  const out: string[] = [];
  let changed = false;
  let i = 0;
  while (i < lines.length) {
    const section = hidden.find((s) => lines[i].trim() === s.heading);
    let end = i + 1;
    while (section && end < lines.length && end - i - 1 < section.maxLines && section.isLine(lines[end].trim())) end += 1;
    if (!section || end === i + 1) {
      out.push(lines[i]);
      i += 1;
      continue;
    }
    changed = true;
    i = end < lines.length && lines[end].trim() === "" ? end + 1 : end;
  }
  return changed ? out.join("\n").trim() : body;
};

// 「공사 기간」 을 끄면 남은 줄의 날짜만 걷는다 — 「· 철거 2026-05-01 ~ 2026-05-05」 → 「· 철거」 · 「· 철거 05-02 — 글」 → 「· 철거 — 글」 · 발췌 「05-02 글」 → 「글」.
const DATED_LINE: readonly { heading: string; re: RegExp; to: string }[] = [
  { heading: DRAFT_HEADING.phases, re: new RegExp(`^${DRAFT_ITEM}(.+?) ${PERIOD_TEXT}$`), to: `${DRAFT_ITEM}$1` },
  { heading: DRAFT_HEADING.logs, re: new RegExp(`^${DRAFT_ITEM}(.+?) \\d{2}-\\d{2} — (.+)$`), to: `${DRAFT_ITEM}$1 — $2` },
];

// 챕터 발췌 앞 「MM-DD 」 — 공개 B 스토리 캡션도 이 모양으로 날짜를 뗀다.
export const EXCERPT_DATE_RE = /^(\d{2}-\d{2})\s+(.+)$/;

// 머리표 바로 아래 「· 」 줄 묶음 안에서 생성 모양인 줄만 바꾼다 — 사용자가 고친 줄은 그대로.
export const withoutDatesBody = (body: string): string => {
  const lines = body.split(/\r?\n/);
  let rule: (typeof DATED_LINE)[number] | undefined;
  let changed = false;
  const out = lines.map((line) => {
    const trimmed = line.trim();
    const heading = DATED_LINE.find((r) => r.heading === trimmed);
    if (heading || !isItemLine(trimmed)) {
      rule = heading;
      return line;
    }
    if (!rule || !rule.re.test(trimmed)) return line;
    changed = true;
    return line.slice(0, line.indexOf(trimmed)) + trimmed.replace(rule.re, rule.to);
  });
  return changed ? out.join("\n") : body;
};

export const withoutDateExcerpt = (line: string): string => {
  const match = EXCERPT_DATE_RE.exec(line.trim());
  return match ? match[2] : line;
};

export const publicExcerptsOf = (excerpts: readonly string[], fields: readonly PortfolioPublicField[]): string[] =>
  fields.includes("period") ? [...excerpts] : excerpts.map(withoutDateExcerpt);

export const publicBodyOf = (body: string, fields: readonly PortfolioPublicField[]): string => {
  const shown = hideSections(body, fields);
  return fields.includes("period") ? shown : withoutDatesBody(shown);
};

// 「공사기간 … · 공정 N개 · 범위 …」 에서 끈 항목 조각만 뺀다 — 조각 전체가 생성 모양일 때만.
export const publicSummaryOf = (summary: string, fields: readonly PortfolioPublicField[]): string => {
  const hidden = SUMMARY_PARTS.filter((part) => !fields.includes(part.field));
  if (hidden.length === 0) return summary;
  let changed = false;
  const lines = summary.split(/\r?\n/).flatMap((line) => {
    const parts = line.split(DRAFT_SUMMARY_SEP);
    const kept = parts.filter((part) => !hidden.some((h) => h.re.test(part.trim())));
    if (kept.length === parts.length) return [line];
    changed = true;
    return kept.length > 0 ? [kept.join(DRAFT_SUMMARY_SEP)] : [];
  });
  return changed ? lines.join("\n").trim() : summary;
};
